import { useState, useCallback } from 'react';
import * as api from 'api/taxiTrip';
import { postMessage, isIOSBridgeAvailable } from 'utils/appPostMessage';
import { compareVersion } from 'utils';

const LIVE_ACTIVITY_EVENT = 'StartRideLiveActivityResult';
const POST_MESSAGE_TYPE = 'rideLiveActivityRequest';
const MIN_SUPPORTED_VERSION = '4.14.0';
const APP_VERSION_PATTERN = /^\d+\.\d+\.\d+$/;

function isSupportedVersion(): boolean {
  const version = window.localStorage.getItem('appVersion');
  if (!version || !APP_VERSION_PATTERN.test(version)) return false;
  return compareVersion(version, MIN_SUPPORTED_VERSION) > 0;
}

export const useLiveActivity = () => {
  const [isProcessing, setIsProcessing] = useState(false);

  const checkAndStartLiveActivity = useCallback(
    async (tripGuid: string) => {
      if (!tripGuid) return;

      // * 僅限有 iOS Bridge 的環境執行
      if (!isIOSBridgeAvailable()) return;
      if (!isSupportedVersion()) return;

      const STORAGE_KEY = 'live_activity_sent_guids';
      let sentGuids: string[] = [];
      try {
        sentGuids = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      } catch (e) {
        sentGuids = [];
      }

      if (sentGuids.includes(tripGuid)) {
        return;
      }

      if (isProcessing) return;
      setIsProcessing(true);

      // 包裝 window event listener 為 Promise，需先掛監聽再送 postMessage
      const tokenPromise = new Promise<{ token: string; fcmToken: string }>((resolve, reject) => {
        const handler = (event: any) => {
          const { token, fcmToken } = event.detail || {};
          if (token && fcmToken) {
            window.removeEventListener(LIVE_ACTIVITY_EVENT, handler);
            resolve({ token, fcmToken });
          }
        };

        window.addEventListener(LIVE_ACTIVITY_EVENT, handler);

        // 設定 10 秒後沒回應則 Timeout
        setTimeout(() => {
          window.removeEventListener(LIVE_ACTIVITY_EVENT, handler);
          reject(new Error('Live Activity token request timed out'));
        }, 10000);
      });

      try {
        // 發送 PostMessage 給 App
        const sent = postMessage({ type: POST_MESSAGE_TYPE });
        if (!sent) {
          throw new Error('Failed to send postMessage to App');
        }

        console.log('Live Activity: Request sent to App');

        // 等待 App 回傳
        const { token, fcmToken } = await tokenPromise;

        // 傳送 Token 給後端
        await api.CallLiveActivity({ tripGuid, token, fcmToken });

        console.log('Live Activity: Backend registered successfully');

        // 成功後才儲存狀態
        sentGuids.push(tripGuid);
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sentGuids));
      } catch (error) {
        console.error('Live Activity Error:', error);
      } finally {
        setIsProcessing(false);
      }
    },
    [isProcessing]
  );

  return {
    checkAndStartLiveActivity,
  };
};
